<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAlerts } from '@/store/alerts.js'
import { useDataStore } from '@/store/data'
import axios from '@/plugins/axios.js'
import Phone from '@/views/components/Phone.vue'
import KiwishLoader from '@/views/components/KiwishLoader.vue'

const router = useRouter()
const route = useRoute()
useI18n()
const dataStore = useDataStore()

// Modo edición
const isEditMode = computed(() => !!route.params.id)
const secretId = computed(() => route.params.id)
const isPhoneUnavailable = computed(() => secretData.value.phone === 'UNAVAILABLE')

// Es el primer secret (no tiene ningún secret)
const isFirstSecret = computed(() => route.query.first === 'true' || secretsCount.value === 0)

// Estado del wizard
const currentStep = ref(7)
const sending = ref(false)
const loading = ref(false)
const errors = ref({})

// Scroll indicator
const contentRef = ref(null)
const showScrollIndicator = ref(false)

// Datos del secret
const secretData = ref({
  id: null,
  name: '',       // Paso 1: Nombre del secreto
  crushName: '',  // Paso 2: Nombre del crush
  prefix: '',     // Paso 3: Prefijo teléfono
  phone: '',      // Paso 3: Teléfono del crush
  hint: '',       // Paso 4: Pista (opcional)
  message: '',    // Paso 5: Mensaje para match (opcional)
  type: 'SEX',
  notify: true
})

// Cargar secreto existente para edición
const loadSecret = async () => {
  if (!isEditMode.value) return

  loading.value = true
  try {
    // Buscar el secreto en el store
    await dataStore.loadSecrets()
    const secret = dataStore.secrets?.find(s => s.id == secretId.value)

    if (secret) {
      secretData.value = {
        id: secret.id,
        name: secret.name || '',
        crushName: secret.crushName || secret.crush_name || '',
        prefix: secret.prefix || '',
        phone: secret.phone || '',
        hint: secret.hint || '',
        message: secret.message || '',
        type: secret.type || 'SEX',
        notify: secret.notify ?? true
      }

      // GTM event para ver un secreto específico
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'view_secret' })
    }
  } catch (e) {
    console.error('Error loading secret:', e)
  } finally {
    loading.value = false
  }
}

// Computed
const secretsCount = computed(() => dataStore.secrets?.length || 0)
const maxSecrets = computed(() => dataStore.numMaxSecrets || 5)
const availableSecrets = computed(() => maxSecrets.value - secretsCount.value)

// Pasos efectivos (en modo edición empieza con paso 0 informativo, sin pasos 6 y 7)
const effectiveSteps = computed(() => {
  if (isEditMode.value) {
    if (isPhoneUnavailable.value) {
      return [0, 1, 2, 4, 5] // Paso 0 + sin paso 3, 6 y 7
    }
    return [0, 1, 2, 3, 4, 5] // Paso 0 + sin paso 6 y 7
  }
  return [1, 2, 3, 4, 5, 6]
})

// Es el último paso del wizard
const isLastStep = computed(() => {
  return currentStepIndex.value === effectiveSteps.value.length - 1
})

const currentStepIndex = computed(() => {
  return effectiveSteps.value.indexOf(currentStep.value)
})

const isStepValid = computed(() => {
  switch (currentStep.value) {
    case 0:
      return true // Paso informativo
    case 1:
      return secretData.value.name.trim().length > 0
    case 2:
      return secretData.value.crushName.trim().length > 0
    case 3:
      return isPhoneUnavailable.value || secretData.value.phone.trim().length >= 6
    case 4:
    case 5:
      return true // Opcionales
    case 6:
      return true
    default:
      return false
  }
})

const gradientClass = computed(() => {
  return currentStep.value === 6 ? 'bg-gradient-match' : 'bg-gradient-secrets'
})

// Navegación
const nextStep = () => {
  const currentIndex = currentStepIndex.value
  if (currentIndex < effectiveSteps.value.length - 1 && isStepValid.value) {
    currentStep.value = effectiveSteps.value[currentIndex + 1]
  }
}

const prevStep = () => {
  const currentIndex = currentStepIndex.value
  if (currentIndex > 0) {
    currentStep.value = effectiveSteps.value[currentIndex - 1]
  }
}

const skipStep = () => {
  // Para pasos opcionales (4 y 5)
  if (currentStep.value === 4) {
    secretData.value.hint = ''
  } else if (currentStep.value === 5) {
    secretData.value.message = ''
  }
  nextStep()
}

// Scroll indicator logic
const keyboardVisible = ref(false)

const checkScrollOverflow = async () => {
  if (!contentRef.value?.$el || keyboardVisible.value) {
    showScrollIndicator.value = false
    return
  }
  const scrollEl = await contentRef.value.$el.getScrollElement()
  if (!scrollEl) return
  const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight
  const isAtBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 10
  showScrollIndicator.value = hasOverflow && !isAtBottom
}

const onContentScroll = () => {
  checkScrollOverflow()
}

// Scroll al input cuando recibe focus (para que no quede detrás del footer/teclado)
const scrollToInput = async (event) => {
  // Ocultar indicador de scroll cuando aparece el teclado
  keyboardVisible.value = true
  showScrollIndicator.value = false

  if (!contentRef.value?.$el) return

  // Esperar a que el teclado aparezca
  await new Promise(resolve => setTimeout(resolve, 300))

  const input = event.target
  if (!input) return

  // Obtener posición del input
  const inputRect = input.getBoundingClientRect()
  const scrollEl = await contentRef.value.$el.getScrollElement()
  if (!scrollEl) return

  // Calcular si el input está oculto por el footer (aprox 80px desde abajo)
  const visibleBottom = window.innerHeight - 80
  if (inputRect.bottom > visibleBottom) {
    const scrollAmount = inputRect.bottom - visibleBottom + 20
    contentRef.value.$el.scrollByPoint(0, scrollAmount, 300)
  }
}

// Detectar cuando se cierra el teclado
const onInputBlur = () => {
  keyboardVisible.value = false
  setTimeout(() => checkScrollOverflow(), 100)
}

// Resetear el wizard al estado inicial
const resetWizard = () => {
  currentStep.value = 1
  secretData.value = {
    id: null,
    name: '',
    crushName: '',
    prefix: '',
    phone: '',
    hint: '',
    message: '',
    type: 'SEX',
    notify: true
  }
  errors.value = {}
}

// Lifecycle
onMounted(async () => {
  // Cargar secreto específico si estamos editando
  if (isEditMode.value) {
    currentStep.value = 0 // Empezar en paso informativo
    await loadSecret()
  } else {
    // Cargar los secretos para el contador solo si no estamos editando
    await dataStore.loadSecrets()
    resetWizard()
  }
  nextTick(() => {
    checkScrollOverflow()
  })
})

// Resetear cuando se navega a esta página
watch(() => route.fullPath, async () => {
  if (route.path === '/app/secrets/new' && !isEditMode.value) {
    resetWizard()
  } else if (route.path.startsWith('/app/secrets/edit/')) {
    // Resetear paso y cargar el secreto al entrar en modo edición
    currentStep.value = 0 // Empezar en paso informativo
    await loadSecret()
  }
})

// Re-check scroll on step change
watch(currentStep, () => {
  nextTick(async () => {
    if (contentRef.value?.$el) {
      contentRef.value.$el.scrollToTop(0)
    }
    checkScrollOverflow()
  })
})

// Enviar secret (crear o actualizar)
const sendSecret = async () => {
  sending.value = true
  errors.value = {}

  try {
    const payload = {
      name: secretData.value.name,
      prefix: secretData.value.prefix,
      phone: secretData.value.phone,
      type: secretData.value.type,
      hint: secretData.value.hint || '',
      message: secretData.value.message || '',
      notify: secretData.value.notify,
      crushName: secretData.value.crushName
    }

    // Si es edición, añadir ID
    if (isEditMode.value && secretData.value.id) {
      payload.id = secretData.value.id
    }

    const response = await axios.post('/secrets/secret', payload)

    if (response.status === 200) {
      // GTM event
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: isEditMode.value ? 'update_secret' : 'add_secret' })

      if (isEditMode.value) {
        // Resetear paso antes de salir
        currentStep.value = 1
        // En modo edición: mostrar toast y volver al listado
        useAlerts().alert = {
          visible: true,
          message: 'secret.updated',
          class: 'alert-success'
        }
        router.push('/app/secrets')
      } else if (response.data.match) {
        // Si hay match, redirigir
        router.push('/app/matchs')
      } else {
        // Recargar secrets y mostrar paso de éxito
        await dataStore.loadSecrets()
        currentStep.value = 7
      }
    }
  } catch (e) {
    if (e.response?.data?.errors) {
      const { errors: formErrors } = e.response.data
      errors.value = Object.keys(formErrors).reduce((acc, key) => {
        acc[key] = formErrors[key][0]
        return acc
      }, {})
    }
    useAlerts().alert = {
      visible: true,
      message: e.response?.data?.message || 'error.general',
      class: 'alert-error'
    }
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <ion-page :class="gradientClass">
    <KiwishLoader :is-open="sending || loading" :message="sending ? $t('sending') : $t('loading')" />

    <!-- Header fijo -->
    <div class="wizard-header">
      <div class="header-spacer"></div>
      <div class="logo-container">
        <img v-if="currentStep === 6" src="/assets/logokw.png" alt="Kiwish" class="logo" />
        <img v-else src="/assets/logokw_dark.png" alt="Kiwish" class="logo" />
      </div>
      <button
        v-if="currentStep < 6"
        class="close-button"
        @click="isFirstSecret ? router.push('/app/fans') : router.push('/app/secrets')"
      >
        <ion-icon name="close-outline"></ion-icon>
      </button>
      <div v-else class="header-spacer"></div>
    </div>

    <!-- Contenido con scroll nativo de Ionic -->
    <ion-content ref="contentRef" class="wizard-content" :scroll-events="true" :scroll-y="true" :fullscreen="true" @ionScroll="onContentScroll">
        <!-- Contador (clicable, a la derecha, dentro del scroll) -->
        <div class="counter" :class="{ 'counter-light': currentStep === 6 }" @click="router.push('/app/secrets')">
          <span class="counter-sent">{{ secretsCount }}/{{ maxSecrets }} {{ $t('wizard.secrets.sent') }}</span>
          <span class="counter-available">{{ availableSecrets }} {{ $t('wizard.secrets.available') }}</span>
        </div>
        <!-- PASO 0: Información de edición (solo en modo edición) -->
        <div v-if="currentStep === 0" class="step-content">
          <h1 class="step-title">{{ $t('wizard.step0.title') }}</h1>
          <p class="step-subtitle">{{ $t('wizard.step0.subtitle') }}</p>
          <p class="step-text">{{ $t('wizard.step0.text1') }}</p>
          <p class="step-text">{{ $t('wizard.step0.text2') }}</p>
          <p class="step-text"><strong>{{ $t('wizard.step0.text3') }}</strong></p>
        </div>

        <!-- PASO 1: Nombre del secreto -->
        <div v-else-if="currentStep === 1" class="step-content">
          <h1 class="step-title">{{ isFirstSecret ? $t('wizard.step1.title.first') : $t('wizard.step1.title') }}</h1>
          <p class="step-subtitle">{{ $t('wizard.step1.subtitle') }}</p>
          <p class="step-text">{{ $t('wizard.step1.text1') }}</p>
          <p class="step-text">
            <strong>{{ $t('wizard.step1.text2.bold') }}</strong> {{ $t('wizard.step1.text2') }}
          </p>
          <p class="step-text">{{ $t('wizard.step1.text3') }}</p>

          <div class="input-group">
            <div class="step-number">1</div>
            <div class="input-wrapper">
              <label class="input-label">{{ $t('wizard.step1.label') }}</label>
              <input
                v-model="secretData.name"
                type="text"
                class="wizard-input"
                :placeholder="$t('wizard.step1.placeholder')"
                maxlength="50"
                @focus="scrollToInput"
                @blur="onInputBlur"
                @keyup.enter="isStepValid && nextStep()"
              />
              <span class="input-hint">{{ $t('wizard.step1.hint') }}</span>
            </div>
          </div>
          <span class="error-text" v-if="errors.name">{{ $t(errors.name) }}</span>
        </div>

        <!-- PASO 2: Nombre del crush -->
        <div v-else-if="currentStep === 2" class="step-content">
          <h1 class="step-title">{{ $t('wizard.step2.title') }}</h1>
          <p class="step-subtitle">{{ $t('wizard.step2.subtitle') }}</p>
          <p class="step-text">{{ $t('wizard.step2.text') }}</p>

          <div class="input-group">
            <div class="step-number">2</div>
            <div class="input-wrapper">
              <label class="input-label">{{ $t('wizard.step2.label') }}</label>
              <input
                v-model="secretData.crushName"
                type="text"
                class="wizard-input"
                :placeholder="$t('wizard.step2.placeholder')"
                maxlength="50"
                @focus="scrollToInput"
                @blur="onInputBlur"
                @keyup.enter="isStepValid && nextStep()"
              />
            </div>
          </div>
        </div>

        <!-- PASO 3: Teléfono del crush -->
        <div v-else-if="currentStep === 3" class="step-content step-content-phone">
          <h1 class="step-title">{{ $t('wizard.step3.title') }}</h1>
          <p class="step-subtitle">{{ $t('wizard.step3.subtitle') }}</p>
          <p class="step-text">{{ $t('wizard.step3.text1') }}</p>
          <p class="step-text">
            {{ $t('wizard.step3.text2.pre') }}
          </p>
          <p class="step-text">
            {{ $t('wizard.step3.text2.1.pre') }}
            <strong class="text-pink">{{ $t('wizard.step3.text2.1.bold') }}</strong>
            {{ $t('wizard.step3.text2.1.post') }}
          </p>
          <p class="step-text">{{ $t('wizard.step3.text3') }}</p>

          <div class="input-group">
            <div class="step-number">3</div>
            <div class="input-wrapper">
              <label class="input-label">{{ $t('wizard.step3.label') }}</label>
              <div class="phone-input-container" @focusin="scrollToInput" @focusout="onInputBlur">
                <Phone
                  v-model:phone="secretData.phone"
                  v-model:prefix="secretData.prefix"
                  :showContactPicker="!isEditMode"
                  :disabled="isEditMode"
                  @enter="isStepValid && nextStep()"
                />
              </div>
            </div>
          </div>
          <span class="error-text" v-if="errors.phone">{{ $t(errors.phone) }}</span>
          <span class="hint-text" v-if="isEditMode">{{ $t('secrets.field.phone.update.hint') }}</span>
        </div>

        <!-- PASO 4: Pista (opcional) -->
        <div v-else-if="currentStep === 4" class="step-content">
          <h1 class="step-title">{{ $t('wizard.step4.title') }}</h1>
          <p class="step-subtitle">
            <strong>{{ $t('wizard.step4.subtitle.bold') }}</strong>
            {{ $t('wizard.step4.subtitle') }}
          </p>
          <p class="step-text step-text-example">{{ $t('wizard.step4.example') }}</p>

          <div class="input-group">
            <div class="step-number">4</div>
            <div class="input-wrapper">
              <label class="input-label">{{ $t('wizard.step4.label') }}</label>
              <textarea
                v-model="secretData.hint"
                class="wizard-textarea"
                :placeholder="$t('wizard.step4.placeholder')"
                maxlength="200"
                rows="3"
                @focus="scrollToInput"
                @blur="onInputBlur"
              ></textarea>
              <button class="skip-inline-button" @click="skipStep">
                {{ $t('wizard.step4.skip') }}
              </button>
            </div>
          </div>
        </div>

        <!-- PASO 5: Mensaje para match (opcional) -->
        <div v-else-if="currentStep === 5" class="step-content">
          <h1 class="step-title">{{ $t('wizard.step5.title') }}</h1>
          <p class="step-subtitle">
            <strong>{{ $t('wizard.step5.subtitle.bold') }}</strong>
            {{ $t('wizard.step5.subtitle') }}
          </p>
          <p class="step-text">{{ $t('wizard.step5.text') }}</p>

          <div class="input-group">
            <div class="step-number">5</div>
            <div class="input-wrapper">
              <label class="input-label">{{ $t('wizard.step5.label') }}</label>
              <textarea
                v-model="secretData.message"
                class="wizard-textarea"
                :placeholder="$t('wizard.step5.placeholder')"
                maxlength="200"
                rows="3"
                @focus="scrollToInput"
                @blur="onInputBlur"
              ></textarea>
              <button class="skip-inline-button" @click="skipStep">
                {{ $t('wizard.step5.skip') }}
              </button>
            </div>
          </div>
        </div>

        <!-- PASO 6: Confirmación -->
        <div v-else-if="currentStep === 6" class="step-content step-final">
          <h1 class="step-title step-title-final">{{ $t('wizard.step6.title') }}</h1>

          <div class="confirm-phone-box">
            <span class="confirm-phone-label">{{ $t('wizard.step6.confirmPhone') }}</span>
            <div class="confirm-phone-number">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ secretData.prefix }} {{ secretData.phone }}</span>
            </div>
          </div>

          <ion-button class="button-pink" expand="block" @click="sendSecret" :disabled="sending">
            {{ $t('wizard.step6.button') }}
          </ion-button>

          <button class="back-text-button" @click="prevStep">
            {{ $t('wizard.step6.back') }}
          </button>

          <div class="kiwi-image-container">
            <img src="/assets/imgs/icons/icon_kiwish.png" alt="Kiwish" class="kiwi-image" />
          </div>

          <p class="inspirational-text">{{ $t('wizard.step6.inspirational') }}</p>
        </div>

        <!-- PASO 7: Éxito -->
        <div v-else-if="currentStep === 7" class="step-content step-success">
          <h1 class="step-title step-title-success">{{ $t('wizard.step7.title') }}</h1>
          <p class="step-subtitle">{{ $t('wizard.step7.subtitle') }}</p>
          <p class="step-text">{{ $t('wizard.step7.text') }}</p>

          <div class="success-button-container">
            <ion-button class="button-success" @click="router.push('/app/secrets')">
              {{ $t('wizard.step7.button') }}
            </ion-button>
          </div>
          <p class="step-text"><strong>{{ $t('wizard.step7.text2.1') }}</strong> {{ $t('wizard.step7.text2.2') }}</p>

          <div class="kiwi-image-container">
            <img src="/assets/imgs/icons/icon_kiwish.png" alt="Kiwish" class="kiwi-image" />
          </div>
        </div>

        <!-- Footer dentro del contenido scrollable -->
        <div class="wizard-footer" v-if="currentStep < 6">
          <div class="footer-arrows">
            <button
              v-if="currentStepIndex > 0"
              class="back-button-footer"
              @click="prevStep"
            >
              <ion-icon name="arrow-back-outline"></ion-icon>
            </button>
            <div v-else class="arrow-placeholder"></div>

            <!-- En modo edición y último paso: botón guardar -->
            <ion-button
              v-if="isEditMode && isLastStep"
              class="save-button"
              :disabled="!isStepValid || sending"
              @click="sendSecret"
            >
              {{ $t('save') }}
            </ion-button>

            <!-- En otros casos: botón siguiente -->
            <button
              v-else
              class="next-button"
              :class="{ 'disabled': !isStepValid }"
              :disabled="!isStepValid"
              @click="nextStep"
            >
              <ion-icon name="arrow-forward-outline"></ion-icon>
            </button>
          </div>

          <button
            class="cancel-button"
            @click="isFirstSecret ? router.push('/app/fans') : router.push('/app/secrets')"
          >
            {{ $t('cancel') }}
          </button>
        </div>
    </ion-content>

    <!-- Indicador de scroll -->
    <transition name="fade">
      <div v-if="showScrollIndicator" class="scroll-indicator">
        <svg width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L9 9L17 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </transition>
  </ion-page>
</template>

<style scoped>
/* Header fijo en la parte superior */
.wizard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: calc(env(safe-area-inset-top, 20px) + 8px);
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;
  background: transparent;
}

.header-spacer {
  width: 40px;
}

.logo-container {
  display: flex;
  justify-content: center;
}

.logo {
  height: 60px;
  width: auto;
}

.close-button {
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: var(--kiwish-pink);
  font-size: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: opacity 0.2s;
}

.close-button:active {
  opacity: 0.6;
}

.counter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  cursor: pointer;
  width: 100%;
  margin-bottom: 20px;
}

.counter-sent {
  font-size: 0.85rem;
  color: var(--text-primary);
  text-decoration: underline;
}

.counter-available {
  font-size: 0.75rem;
  color: var(--text-primary);
}

.counter-light .counter-sent,
.counter-light .counter-available {
  color: var(--bg-card);
}

/* Contenido con ion-content */
.wizard-content {
  --background: transparent;
  --padding-start: 24px;
  --padding-end: 24px;
  --padding-bottom: 20px;
  --overflow: auto;
}

.step-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}

.step-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--kiwish-pink);
  margin-bottom: 12px;
  line-height: 1.2;
}

.step-subtitle {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  line-height: 1.3;
}

.step-text {
  font-size: 0.95rem;
  font-weight: 400;
  color: var(--text-primary);
  margin-bottom: 12px;
  line-height: 1.4;
}

.step-text-example {
  font-style: italic;
  color: var(--text-secondary);
}

.text-pink {
  color: var(--kiwish-pink);
}

/* Input group */
.input-group {
  display: flex;
  align-items: flex-start;
  margin-top: 24px;
  gap: 12px;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid var(--kiwish-pink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: var(--kiwish-pink);
  flex-shrink: 0;
}

.input-wrapper {
  flex: 1;
}

.input-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--kiwish-pink);
  margin-bottom: 8px;
}

.wizard-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 1rem;
  background: var(--bg-white);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;
}

.wizard-input:focus {
  border-color: var(--kiwish-pink);
}

.wizard-textarea {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 1rem;
  background: var(--bg-white);
  color: var(--text-primary);
  outline: none;
  resize: none;
  font-family: inherit;
  transition: border-color 0.2s;
}

.wizard-textarea:focus {
  border-color: var(--kiwish-pink);
}

.phone-input-container {
  background: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
}

.step-content-phone {
  padding-bottom: 40px;
}

.error-text {
  color: var(--color-error);
  font-size: 0.85rem;
  margin-top: 8px;
  display: block;
}

.hint-text {
  color: var(--kiwish-pink);
  font-size: 0.85rem;
  margin-top: 8px;
  display: block;
}

.input-hint {
  color: var(--text-primary);
  font-size: 0.8rem;
  margin-top: 8px;
  display: block;
  opacity: 0.7;
}

/* Footer dentro del scroll */
.wizard-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  padding-bottom: calc(env(safe-area-inset-bottom, 12px) + 12px);
  margin-top: auto;
  gap: 16px;
}

.footer-arrows {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.arrow-placeholder {
  width: 40px;
}

.back-button-footer {
  background: none;
  border: none;
  color: var(--kiwish-pink);
  font-size: 2.5rem;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  line-height: 1;
  transition: opacity 0.2s, transform 0.2s;
}

.back-button-footer:active {
  transform: scale(0.95);
}

.cancel-button {
  background: none;
  border: none;
  color: var(--kiwish-pink);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;
  text-align: center;
}

.skip-inline-button {
  background: none;
  border: none;
  color: var(--kiwish-pink);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  padding: 12px 0 0 0;
  text-align: left;
}

.next-button {
  background: none;
  border: none;
  color: var(--kiwish-pink);
  font-size: 2.5rem;
  font-weight: 700;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  transition: opacity 0.2s, transform 0.2s;
}

.next-button:active {
  transform: scale(0.95);
}

.next-button.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Botón guardar (modo edición) */
.save-button {
  --background: var(--kiwish-pink);
  --background-activated: var(--kiwish-pink);
  --background-hover: var(--kiwish-pink);
  --color: #fff;
  --border-radius: 12px;
  --padding-start: 24px;
  --padding-end: 24px;
  font-weight: 600;
  height: 40px;
}

.save-button::part(native) {
  background: var(--kiwish-pink);
  color: #fff;
}

/* Paso final (6) */
.step-final {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 0px;
}

.step-title-final {
  color: var(--bg-card);
  font-size: 1.3rem;
  max-width: 300px;
  line-height: 1.9rem;
}

.confirm-phone-box {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.confirm-phone-label {
  color: var(--bg-card);
  font-size: 1rem;
  line-height: 1.2rem;
  opacity: 1;
  display: block;
  width: 85%;
}

.confirm-phone-number {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.2);
  padding: 7px 20px;
  border-radius: 12px;
  color: var(--bg-card);
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.confirm-phone-number svg {
  flex-shrink: 0;
}

/* Botón enviar (estilo app) */
.button-pink {
  --background: #EDEAD5 !important;
  --background-activated: #EDEAD5 !important;
  --background-hover: #EDEAD5 !important;
  --color: #EE2D7D !important;
  margin-top: 40px;
  width: 100%;
  max-width: 280px;
}

.button-pink::part(native) {
  background: #EDEAD5 !important;
  background-color: #EDEAD5 !important;
  color: #EE2D7D !important;
  border: 1px solid #EE2D7D !important;
  border-color: #5A695B !important;
}

.back-text-button {
  background: none;
  border: none;
  color: var(--bg-card);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 20px;
  padding: 8px;
}

.kiwi-image-container {
  margin-top: 32px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kiwi-image {
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
}

.inspirational-text {
  color: var(--bg-card);
  font-size: 1rem;
  line-height: 1.4;
  text-align: center;
  margin-top: 20px;
  padding: 0 20px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 20px);
}

/* Gradientes de fondo (de abajo hacia arriba) */
.bg-gradient-secrets {
  background: linear-gradient(to top, var(--secrets-gradient-end), var(--secrets-gradient-start));
}

.bg-gradient-match {
  background: linear-gradient(to top, var(--match-gradient-end), var(--match-gradient-start));
}

/* Scroll indicator */
.scroll-indicator {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 82, 135, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.3rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: bounce 1.5s ease-in-out infinite;
  pointer-events: none;
}

@keyframes bounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(6px); }
}

/* Transition para el indicador */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Paso 7: Éxito */
.step-success {
  text-align: left;
  padding-top: 20px;
}

.step-title-success {
  color: var(--kiwish-pink);
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.success-button-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  margin-bottom: 30px;
}

.button-success {
  --background: var(--kiwish-pink);
  --background-activated: var(--kiwish-pink);
  --background-hover: var(--kiwish-pink);
  --color: #fff;
  --border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  width: 100%;
  max-width: 280px;
}

.button-success::part(native) {
  background: var(--kiwish-pink);
  color: #fff;
}
</style>
